import { Table, Button } from 'react-bootstrap';
import { useGetStocksQuery } from '../../slices/stocksApiSlice';
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice';
import { useGetTransfersQuery } from '../../slices/transfersApiSlice';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useGetIncomesQuery } from '../../slices/incomesApiSlice';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useState, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';

const WorkingCapital = () => {
  const { data: stocksData, isLoading, error } = useGetStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();

  const stocks = useMemo(() => stocksData?.stocks || [], [stocksData]);
  const supplies = useMemo(() => suppliesData?.supplies || [], [suppliesData]);
  const transfers = useMemo(() => transfersData?.transfers || [], [transfersData]);

  const [stockValues, setStockValues] = useState({});

  const uniqueDates = useMemo(() => {
    return stocks.length > 0
      ? [...new Set(stocks.map(stock => new Date(stock.date).toISOString().split('T')[0]))]
      : [];
  }, [stocks]);

  const sortedDates = useMemo(
    () => uniqueDates.sort((a, b) => new Date(a) - new Date(b)),
    [uniqueDates]
  );

  const date1 = sortedDates;
  const date2 = [...sortedDates.slice(1), sortedDates[sortedDates.length - 1]];

  const { data: expensesData } = useGetExpensesQuery({
    startDate: date1,
    endDate: date2,
  });

  const { data: incomesData } = useGetIncomesQuery({
    startDate: date1,
    endDate: date2,
  });

  const expenses = useMemo(() => expensesData?.expenses || [], [expensesData]);
  const incomes = useMemo(() => incomesData?.incomes || [], [incomesData]);

  const calculateStockValue = useCallback(
    date => {
      return stocks
        .filter(stock => new Date(stock.date).toISOString().split('T')[0] === date)
        .flatMap(stock => stock.stockItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [stocks]
  );

  const calculateSupplyValue = useCallback(
    (start, cutoff) => {
      return supplies
        .filter(s => {
          const d = new Date(s.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(s => s.supplyItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [supplies]
  );

  const calculateTransferValue = useCallback(
    (start, cutoff) => {
      return transfers
        .filter(t => {
          const d = new Date(t.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(t => t.transferItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [transfers]
  );

  const calculateExpenseValue = useCallback(
    (start, cutoff) => {
      return expenses
        .filter(e => {
          const d = new Date(e.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [expenses]
  );

  const calculateIncomeValue = useCallback(
    (start, cutoff) => {
      return incomes
        .filter(i => {
          const d = new Date(i.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, i) => acc + Number(i.amount), 0);
    },
    [incomes]
  );

  const calculateSalesCredit = (start, cutoff) => {
    return expenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return e.category === 'SalesCredit' && d >= start && d <= cutoff;
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  const calculateSalesPayment = (start, cutoff) => {
    return expenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return e.category === 'SalesPayment' && d >= start && d <= cutoff;
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  const calculateCumulativeReceivables = (start, cutoff) => {
    return expenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return (
          d >= start &&
          d <= cutoff &&
          (e.category === 'SalesCredit' || e.category === 'SalesPayment')
        );
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  const recalculateStockValues = () => {
    const updated = date1.reduce((acc, date, index) => {
      const start = date;
      const end = date2[index];

      const cutoff = new Date(end);
      cutoff.setDate(cutoff.getDate() - 1);
      const cutoffDate = cutoff.toISOString().split('T')[0];

      const opening = calculateStockValue(start);
      const supply = calculateSupplyValue(start, cutoffDate);
      const transfer = calculateTransferValue(start, cutoffDate);
      const expense = calculateExpenseValue(start, cutoffDate);
      const income = calculateIncomeValue(start, cutoffDate);
      const credit = calculateSalesCredit(start, cutoffDate);
      const payment = calculateSalesPayment(start, cutoffDate);
      const closing = calculateStockValue(end);

      const net = opening + supply + expense - transfer - closing;
      const varValue = income - net;

      const cumulative = calculateCumulativeReceivables(date1[0], cutoffDate);

      acc[date] = {
        openingStockValue: opening,
        supplyValue: supply,
        transferValue: transfer,
        expenseValue: expense,
        incomeValue: income,
        expenseSalesCreditValue: credit,
        expenseSalesPaymentValue: payment,
        increaseInReceivables: credit + payment,
        closingStockValue: closing,
        netValue: net,
        varValue,
        reValue: cumulative,
      };

      return acc;
    }, {});

    setStockValues(updated);
  };

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message}</Message>;
  if (stocks.length === 0) return <Message>No stock data</Message>;

  // EXPORT TO EXCEL
    // -----------------------------
  
    const exportToExcel = () => {
  const tableData = date1.map((date, index) => {
    const v = stockValues[date] || {};
    return {
      Date1: date,
      Date2: date2[index],
      'Opening Stock Value': v.openingStockValue,
      'Supply Value': v.supplyValue,
      'Transfer Value': v.transferValue,
      'Expense Value': v.expenseValue,
      'Closing Stock Value': v.closingStockValue,
      'Net Value': v.netValue,
      'Income Value': v.incomeValue,
      'Var': v.varValue,
      'Credit': v.expenseSalesCreditValue,
      'Payment': v.expenseSalesPaymentValue,
      'Receivable Inc': v.increaseInReceivables,
      'RVar': v.reValue,
    };
  });

  const ws = XLSX.utils.json_to_sheet(tableData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Working Capital');
  XLSX.writeFile(wb, 'WorkingCapital.xlsx');
};


  return (
    <div>
      <h1>Working Capital</h1>
      <Button onClick={recalculateStockValues}>Recalculate</Button>
       <Button onClick={exportToExcel}>Export to Excel</Button>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening</th>
            <th>Supply</th>
            <th>Transfer</th>
            <th>Expense</th>
            <th>Closing</th>
            <th>Net</th>
            <th>Income</th>
            <th>Var</th>
            <th>Credit</th>
            <th>Payment</th>
            <th>Receivable Inc</th>
            <th>RVar</th>
          </tr>
        </thead>
        <tbody>
          {date1.map((date, index) => {
            const v = stockValues[date] || {};
            return (
              <tr key={date}>
                <td>{date}</td>
                <td>{date2[index]}</td>
                <td>{v.openingStockValue}</td>
                <td>{v.supplyValue}</td>
                <td>{v.transferValue}</td>
                <td>{v.expenseValue}</td>
                <td>{v.closingStockValue}</td>
                <td>{v.netValue}</td>
                <td>{v.incomeValue}</td>
                <td>{v.varValue}</td>
                <td>{v.expenseSalesCreditValue}</td>
                <td>{v.expenseSalesPaymentValue}</td>
                <td>{v.increaseInReceivables}</td>
                <td>{v.reValue}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default WorkingCapital;